import React, { useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiAssetUrl, deleteMyVenuePhoto, uploadMyVenuePhoto } from '../lib/api';
import { Button } from './ui/button';

/* The venue's photo, shown behind its name on every gig page it posts. Saved on its own, the
   moment it is picked, not with the profile's Save button. The browser shrinks it before it
   leaves (2000px wide, JPEG), so a 6MB phone photo goes up as ~300KB, and anything the
   browser can open (including an iPhone's HEIC in Safari) arrives as a JPEG. */
const MAX_WIDTH = 2000;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    img.src = url;
  });
}

async function shrinkPhoto(file) {
  const img = await loadImage(file);
  const scale = Math.min(1, MAX_WIDTH / img.naturalWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error('unreadable');
  return blob;
}

export function VenuePhotoUpload({ photoUrl, onSaved }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'upload' | 'remove' | null

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy('upload');
    try {
      let blob;
      try {
        blob = await shrinkPhoto(file);
      } catch {
        toast.error("That file can't be opened as a photo. Try a JPG or PNG.");
        return;
      }
      const form = new FormData();
      form.append('file', blob, 'venue-photo.jpg');
      const res = await uploadMyVenuePhoto(form);
      onSaved(res.data);
      toast.success(photoUrl ? 'Photo Replaced' : 'Photo Saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save photo');
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setBusy('remove');
    try {
      const res = await deleteMyVenuePhoto();
      onSaved(res.data);
      toast.success('Photo Removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    } finally {
      setBusy(null);
    }
  };

  const src = apiAssetUrl(photoUrl);

  return (
    <div className="space-y-4" data-testid="venue-photo">
      {/* The same 16:9 frame the gig page crops it to, so what you see here is what a musician sees. */}
      <div className="relative w-full overflow-hidden rounded-xl border border-border bg-secondary" style={{ aspectRatio: '16 / 9' }}>
        {src ? (
          <img src={src} alt="Your venue" className="absolute inset-0 h-full w-full object-cover" data-testid="venue-photo-preview" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <ImagePlus className="h-8 w-8" aria-hidden />
            <span className="text-sm">No Photo Yet</span>
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Shown behind your venue's name on your gigs. A wide, landscape shot of the room or the stage works best.
      </p>
      <div className="flex flex-wrap gap-3">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="venue-photo-input" />
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={!!busy}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="venue-photo-upload-btn"
        >
          <ImagePlus className="h-4 w-4 mr-2" aria-hidden />
          {busy === 'upload' ? 'Uploading…' : photoUrl ? 'Replace Photo' : 'Upload Photo'}
        </Button>
        {photoUrl && (
          <Button type="button" variant="outline" onClick={remove} disabled={!!busy} data-testid="venue-photo-remove-btn">
            <Trash2 className="h-4 w-4 mr-2" aria-hidden />
            {busy === 'remove' ? 'Removing…' : 'Remove Photo'}
          </Button>
        )}
      </div>
    </div>
  );
}
